import dynamic from 'next/dynamic';
import type { ReactElement } from 'react';
import { useEffect } from 'react';

import useCheckInitialRender from '@/components/hooks/useCheckInitialRender';
import { useRegisterDialog } from '@/components/hooks/useRegisterDialog';
import { useScrollByParamsAndHash } from '@/components/hooks/useScrollByParamsAndHash';
import CustomMetatag from '@/components/layouts/custom-metatag';
import Layout from '@/components/layouts/lcv';
import { ProductHeroBanner } from '@/components/sections/einstein/product/banner';
import { MODEL_YEAR_2025_1, SPARK_CLASS } from '@/const/product';
import {
  DATA,
  USP_DETAILS_COMPONENTS,
} from '@/contents/lcv/product/maxforce/spark';
import {
  parseMenuData,
  parseModelData,
  sortSpark,
} from '@/contents/lcv/product/utility';
import { getLCVModelByClass } from '@/services/contentstack';
import { Main } from '@/stitches.config';

const ProductNavigation = dynamic(
  () => import('@/components/sections/my24/product/navigation'),
  {
    ssr: false,
  }
);
const ProductEngine = dynamic(
  () =>
    import('@/components/sections/einstein/product/engine').then(
      (mod) => mod.LCVProductEngine
    ),
  {
    ssr: false,
  }
);
const ProductGear = dynamic(
  () =>
    import('@/components/sections/einstein/product/gear').then(
      (mod) => mod.LCVProductGear
    ),
  {
    ssr: false,
  }
);
const ProductExploreCarousel = dynamic(
  () =>
    import('@/components/sections/einstein/product/explore-carousel').then(
      (mod) => mod.ProductExploreCarousel
    ),
  {
    ssr: false,
  }
);
const ProductCustomize = dynamic(
  () =>
    import('@/components/shared/product-customize').then(
      (mod) => mod.ProductCustomize
    ),
  {
    ssr: false,
  }
);
const ProductLineup = dynamic(
  () =>
    import('@/components/sections/einstein/product/lineup').then(
      (mod) => mod.LCVProductLineup
    ),
  {
    ssr: false,
  }
);
const ProductCta = dynamic(() => import('@/components/shared/cta'), {
  ssr: false,
});
const FooterLCVGroup = dynamic(
  () => import('@/components/layouts/lcv/footer-group'),
  {
    ssr: false,
  }
);

export async function getStaticProps() {
  const MODEL_RESPONSE = await getLCVModelByClass(
    SPARK_CLASS,
    MODEL_YEAR_2025_1
  );
  const MODEL = sortSpark(parseModelData(MODEL_RESPONSE?.data?.entries || []));

  return {
    props: JSON.parse(
      JSON.stringify({
        MODEL,
      })
    ),
  };
}

export default function Page({ MODEL }) {
  const MENU = parseMenuData(DATA || {});

  useScrollByParamsAndHash(56);
  const { api: registerDialog, component: registerDialogComponent } =
    useRegisterDialog({
      theme: 'dark',
      lcvModel: 'pickup',
      model: 'spark',
    });

  const isInitialRender = useCheckInitialRender();
  useEffect(() => {
    if (!isInitialRender || !registerDialog) return;
    registerDialog.addButtonClick(
      'navigation',
      DATA?.PRODUCT_NAVIGATION_DATA.buttons ?? []
    );
  }, [isInitialRender, registerDialog]);

  return (
    <>
      {!!(
        DATA?.META_TAG_DATA?.title ||
        DATA?.META_TAG_DATA?.description ||
        DATA?.META_TAG_DATA?.img?.url
      ) && (
        <CustomMetatag
          title={DATA?.META_TAG_DATA?.title}
          description={DATA?.META_TAG_DATA?.description}
          imgURL={DATA?.META_TAG_DATA?.img?.url}
        />
      )}
      <Main theme="dark">
        <ProductHeroBanner {...DATA?.PRODUCT_HERO_BANNER_DATA} />
        <ProductNavigation
          {...DATA?.PRODUCT_NAVIGATION_DATA}
          menuModels={MENU}
        />
        <ProductEngine {...DATA?.PRODUCT_ENGINE_DATA} />
        <ProductGear {...DATA?.SHARED_PRODUCT_GEAR_DATA} />
        <ProductExploreCarousel
          {...DATA?.PRODUCT_EXPLORE_DATA}
          uspDetails={USP_DETAILS_COMPONENTS}
        />
        <ProductCustomize {...DATA?.PRODUCT_CUSTOMIZE_DATA} listModel={MODEL} />
        <ProductLineup {...DATA?.PRODUCT_LINEUP_DATA} />
        <ProductCta {...DATA?.PRODUCT_CTA_DATA} />
        {registerDialogComponent}
      </Main>
      <FooterLCVGroup />
    </>
  );
}

Page.getLayout = function getLayout(page: ReactElement) {
  return <Layout isTransparentNavigation>{page}</Layout>;
};
